import { io } from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.connectionAttempts = 0;
    this.maxConnectionAttempts = 5;
    
    // Track registered listeners to prevent duplicates
    this.registeredListeners = new Map();
    this.connectionInProgress = false;
  }

  connect(token) {
    // Prevent multiple connection attempts
    if (this.connectionInProgress) {
      console.log('🔌 Connection already in progress');
      return this.socket;
    }

    // If already connected with the same token, return existing socket
    if (this.socket && this.isConnected) {
      console.log('🔌 Socket already connected');
      return this.socket;
    }

    const socketUrl = import.meta.env.VITE_API_BASE_URL2 || 'http://localhost:3338';
    console.log('🔌 MERCHANT - Connecting to socket server:', socketUrl);

    // Disconnect existing socket if any
    if (this.socket) {
      this.disconnect();
    }

    this.connectionInProgress = true;

    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxConnectionAttempts,
      reconnectionDelay: 1000,
      timeout: 20000
    });

    this.setupEventListeners();
    return this.socket;
  }

  setupEventListeners() {
    // Clear any existing listeners to prevent duplicates
    this.clearAllListeners();

    this.socket.on('connect', () => {
      console.log('✅ MERCHANT - Connected to server successfully! Socket ID:', this.socket.id);
      this.isConnected = true;
      this.connectionAttempts = 0;
      this.connectionInProgress = false;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ MERCHANT - Disconnected from server:', reason);
      this.isConnected = false;
      this.connectionInProgress = false;
    });

    this.socket.on('connect_error', (error) => {
      this.connectionAttempts++;
      console.error('❌ MERCHANT - Connection error:', error.message);
      this.isConnected = false;
      this.connectionInProgress = false;
    });

    this.socket.on('system_notification', (notification) => {
      console.log('🔔🎯 MERCHANT - SYSTEM NOTIFICATION RECEIVED:', {
        id: notification.id,
        title: notification.title,
        type: notification.notificationType?.name,
        timestamp: new Date().toISOString(),
        socketId: this.socket.id
      });

      if (this.registeredListeners.has('system_notification')) {
        this.registeredListeners.get('system_notification')(notification);
      }
    });

    // Test response handler
    this.socket.on('test_response', (data) => {
      console.log('🧪 MERCHANT - Test response from server:', data);
    });

    // Online users handler
    this.socket.on('online_users', (users) => {
      console.log('👥 MERCHANT - Online users received:', users.length);
      if (this.registeredListeners.has('online_users')) {
        this.registeredListeners.get('online_users')(users);
      }
    });

    // Product created handler for merchant
    this.socket.on('product_created', (notification) => {
      console.log('📦 MERCHANT - Product created notification:', notification);
      if (this.registeredListeners.has('product_created')) {
        this.registeredListeners.get('product_created')(notification);
      }
    });

    // Order notifications for merchant
    this.socket.on('order_notification', (notification) => {
      console.log('🛒 MERCHANT - Order notification for merchant:', notification);
      if (this.registeredListeners.has('order_notification')) {
        this.registeredListeners.get('order_notification')(notification);
      }
    });
  }

  // Enhanced event registration with duplicate prevention
  onSystemNotification(callback) {
    console.log('🎯 MERCHANT - Registering system notification listener');
    this.registeredListeners.set('system_notification', callback);
    
    if (this.socket) {
      this.socket.off('system_notification');
      this.socket.on('system_notification', callback);
    }
  }

  onOnlineUsers(callback) {
    this.registeredListeners.set('online_users', callback);
    if (this.socket) {
      this.socket.off('online_users');
      this.socket.on('online_users', callback);
    }
  }

  onProductCreated(callback) {
    this.registeredListeners.set('product_created', callback);
    if (this.socket) {
      this.socket.off('product_created');
      this.socket.on('product_created', callback);
    }
  }

  onOrderNotification(callback) {
    this.registeredListeners.set('order_notification', callback);
    if (this.socket) {
      this.socket.off('order_notification');
      this.socket.on('order_notification', callback);
    }
  }

  offSystemNotification(callback) {
    this.registeredListeners.delete('system_notification');
    if (this.socket) {
      this.socket.off('system_notification', callback);
    }
  }

  offOnlineUsers(callback) {
    this.registeredListeners.delete('online_users');
    if (this.socket) {
      this.socket.off('online_users', callback);
    }
  }

  offProductCreated(callback) {
    this.registeredListeners.delete('product_created');
    if (this.socket) {
      this.socket.off('product_created', callback);
    }
  }

  offOrderNotification(callback) {
    this.registeredListeners.delete('order_notification');
    if (this.socket) {
      this.socket.off('order_notification', callback);
    }
  }

  clearAllListeners() {
    this.registeredListeners.clear();
    if (this.socket) {
      this.socket.removeAllListeners();
    }
  }

  registerUser(userData) {
    if (this.socket && this.isConnected) {
      console.log('👤 MERCHANT - Registering merchant user with socket:', {
        userId: userData.userId,
        role: userData.role,
        username: userData.username,
        socketId: this.socket.id
      });
      
      this.socket.emit('register', {
        userId: userData.userId.toString(),
        role: userData.role,
        username: userData.username,
        email: userData.email,
        type: 'merchant' 
      });
    } else {
      console.warn('⚠️ MERCHANT - Cannot register merchant user: Socket not connected');
    }
  }

  disconnect() {
    if (this.socket) {
      console.log('🔌 MERCHANT - Disconnecting socket...');
      this.clearAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.connectionInProgress = false;
    }
  }

  getSocket() {
    return this.socket;
  }

  getConnectionStatus() {
    return this.isConnected;
  }

  testSocket() {
    if (this.socket && this.isConnected) {
      this.socket.emit('test', { 
        message: 'Test from merchant client', 
        timestamp: Date.now() 
      });
    }
  }
}

// Create a new instance specifically for merchant
const merchantSocketService = new SocketService();
export default merchantSocketService;